// Globals
import { useCallback, useEffect, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import debounce from 'lodash.debounce';

// Store
import { useAppDispatch, useAppState } from '@/store';
import {
  getCategoryStats,
  changeCategoryStatsQuery,
  selectCategories,
  selectCategoryStats,
  selectCategoryStatsQuery,
  selectCategoryStatsIsLoading,
} from '../../feature';

// Models
import { ICategory } from '@/models/categories.model';
import { ROUTES } from '@/models/router.model';

// Components
import { useForm } from '@/components';
import { Skeleton } from '@/ui';
import {
  StyledPanel,
  StyledForm,
  StyledFormItem,
  StyledRangePicker,
  StyledTreeSelect,
  StyledTable,
  StyledTh,
  StyledTd,
  StyledTr,
  StyledTotalTd,
  StyledContractorsBlock,
  StyledContractorsTitle,
  StyledContractorsList,
  StyledContractorItem,
  StyledContractorCount,
} from './CategoryStatsPanelStyles';

// Helpers
import { DateFormats } from '@/constants/dates';
import { formatDateForApiRequest } from '@/utils';

type TreeNode = { title: string; value: string; key: string; children?: TreeNode[] };

const buildTreeData = (categories: ICategory[]): TreeNode[] =>
  categories.map((cat) => ({
    title: cat.name,
    value: cat.id,
    key: cat.id,
    ...(cat.sub_categories?.length ? { children: buildTreeData(cat.sub_categories) } : {}),
  }));

const CategoryStatsPanel = () => {
  const { t } = useTranslation(['categories']);
  const [dateForm] = useForm();
  const dispatch = useAppDispatch();
  const categoryStats = useAppState(selectCategoryStats);
  const categoryStatsQuery = useAppState(selectCategoryStatsQuery);
  const isLoading = useAppState(selectCategoryStatsIsLoading);
  const categories = useAppState(selectCategories);

  const treeData = useMemo(() => buildTreeData(categories ?? []), [categories]);

  useEffect(() => {
    dispatch(getCategoryStats({}));
  }, []);

  const handleFetch = useCallback(
    debounce((query: typeof categoryStatsQuery) => {
      dispatch(getCategoryStats(query));
    }, 400),
    [dispatch],
  );

  const handleDateChange = (_: unknown, dateStrings: [string, string]) => {
    const newQuery = {
      ...categoryStatsQuery,
      date_from: formatDateForApiRequest(dateStrings[0], DateFormats.DATE_WITHOUT_HOURS) || undefined,
      date_to: formatDateForApiRequest(dateStrings[1], DateFormats.DATE_WITHOUT_HOURS) || undefined,
    };
    dispatch(changeCategoryStatsQuery(newQuery));
    handleFetch(newQuery);
  };

  const handleCategoryChange = (value: unknown) => {
    const newQuery = {
      ...categoryStatsQuery,
      category_id: (value as string) || undefined,
    };
    dispatch(changeCategoryStatsQuery(newQuery));
    handleFetch(newQuery);
  };

  const rows = [
    { label: t('stats_level_1'), data: categoryStats?.level_1 },
    { label: t('stats_level_2'), data: categoryStats?.level_2 },
    { label: t('stats_level_3'), data: categoryStats?.level_3 },
  ];

  const levelsTotal = categoryStats
    ? {
        total:
          (categoryStats.level_1?.total ?? 0) +
          (categoryStats.level_2?.total ?? 0) +
          (categoryStats.level_3?.total ?? 0),
        active:
          (categoryStats.level_1?.active ?? 0) +
          (categoryStats.level_2?.active ?? 0) +
          (categoryStats.level_3?.active ?? 0),
        inactive:
          (categoryStats.level_1?.inactive ?? 0) +
          (categoryStats.level_2?.inactive ?? 0) +
          (categoryStats.level_3?.inactive ?? 0),
      }
    : null;

  return (
    <StyledPanel>
      <StyledForm form={dateForm} layout="vertical">
        <StyledFormItem label={t('stats_period_label')} name="date_range">
          <StyledRangePicker
            format={DateFormats.DATE_WITHOUT_HOURS}
            placeholder={[t('stats_date_from'), t('stats_date_to')]}
            onChange={handleDateChange}
          />
        </StyledFormItem>
      </StyledForm>

      {isLoading ? (
        <Skeleton active paragraph={{ rows: 4 }} />
      ) : (
        <>
          <StyledTable>
            <thead>
              <StyledTr>
                <StyledTh>{t('stats_level_label')}</StyledTh>
                <StyledTh>{t('stats_active')}</StyledTh>
                <StyledTh>{t('stats_inactive')}</StyledTh>
                <StyledTh>{t('stats_total')}</StyledTh>
              </StyledTr>
            </thead>
            <tbody>
              {rows.map((row) => (
                <StyledTr key={row.label}>
                  <StyledTd>{row.label}</StyledTd>
                  <StyledTd>{row.data?.active ?? '—'}</StyledTd>
                  <StyledTd>{row.data?.inactive ?? '—'}</StyledTd>
                  <StyledTd>{row.data?.total ?? '—'}</StyledTd>
                </StyledTr>
              ))}
              <StyledTr>
                <StyledTotalTd>{t('stats_levels_total')}</StyledTotalTd>
                <StyledTotalTd>{levelsTotal?.active ?? '—'}</StyledTotalTd>
                <StyledTotalTd>{levelsTotal?.inactive ?? '—'}</StyledTotalTd>
                <StyledTotalTd>{levelsTotal?.total ?? '—'}</StyledTotalTd>
              </StyledTr>
              <StyledTr>
                <td colSpan={4} style={{ padding: '0.5rem 0' }}>
                  <StyledTreeSelect
                    allowClear
                    showSearch
                    treeData={treeData}
                    treeDefaultExpandAll
                    value={categoryStatsQuery.category_id ?? null}
                    dropdownStyle={{ maxHeight: 400, overflow: 'auto' }}
                    placeholder={t('stats_category_placeholder')}
                    filterTreeNode={(input, node) =>
                      (node.title as string)?.toLowerCase().includes(input.toLowerCase())
                    }
                    onChange={handleCategoryChange}
                  />
                </td>
              </StyledTr>
              <StyledTr>
                <StyledTd>{t('stats_sku_label')}</StyledTd>
                <StyledTd>{categoryStats?.sku?.active ?? '—'}</StyledTd>
                <StyledTd>{categoryStats?.sku?.inactive ?? '—'}</StyledTd>
                <StyledTd>{categoryStats?.sku?.total ?? '—'}</StyledTd>
              </StyledTr>
            </tbody>
          </StyledTable>
          {categoryStatsQuery.category_id && categoryStats?.contractors && categoryStats.contractors.length > 0 && (
            <StyledContractorsBlock>
              <StyledContractorsTitle>{t('stats_contractors_label')}</StyledContractorsTitle>
              <StyledContractorsList>
                {categoryStats.contractors.map((contractor) => (
                  <StyledContractorItem
                    key={contractor.id}
                    $clickable={!!contractor.partner_id}
                    onClick={() => {
                      if (contractor.partner_id) {
                        window.open(`${ROUTES.PARTNER_DETAILS_INDEX}/${contractor.partner_id}`, '_blank');
                      }
                    }}
                  >
                    {contractor.title}
                    <StyledContractorCount>{contractor.product_count}</StyledContractorCount>
                  </StyledContractorItem>
                ))}
              </StyledContractorsList>
            </StyledContractorsBlock>
          )}
        </>
      )}
    </StyledPanel>
  );
};

export default CategoryStatsPanel;
